import { Menu, MenuItem, Tooltip } from "@mui/material";
import Help from "assets/img/svg/navbar/help";
import classNames from "classnames";
import RenderIf from "helpers/RenderIf";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { NavigationPortalType } from "../types";
import NavigationButton from "./NavigationButton";

interface HelpMenuProps {
    /** Portal type for appropriate help desk URL */
    portalType: NavigationPortalType;
    /** Whether nav links are provided (for changelog navigation state) */
    hasNavLinks?: boolean;
    /** Callback when menu action completes (for mobile nav close) */
    onActionComplete?: () => void;
    /** Style variant */
    variant?: "default" | "book";
    /** Whether the navigation is collapsed */
    isCollapsed?: boolean;
}

const HELPDESK_URLS: Record<string, string> = {
    press: "https://presse-helpdesk.crealo.app",
    audiovisual: "https://presse-helpdesk.crealo.app",
    beneficiary: "https://beneficiaires-helpdesk.crealo.app/fr/",
    book: "https://helpdesk.crealo.app/fr/",
    urssaf: "https://helpdesk.crealo.app/fr/",
};

const menuItemStyle = {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 400,
    paddingBlock: "8px",
    transition: "all 0.2s ease",
};

/**
 * Help menu with changelog, documentation, and TOS links
 * Shows only icon when collapsed
 */
const HelpMenu = ({
    portalType,
    hasNavLinks = false,
    onActionComplete,
    variant = "default",
    isCollapsed = false,
}: HelpMenuProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget as HTMLElement);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChangelog = () => {
        const navbarType = portalType === "book" ? "book" : "bp";
        navigate("/changelog", {
            state: {
                hasNavbar: hasNavLinks,
                navbarType,
            },
        });
        handleClose();
        onActionComplete?.();
    };

    const handleDocumentation = () => {
        window.open(HELPDESK_URLS[portalType] || HELPDESK_URLS.book, "_blank");
        handleClose();
    };

    const handleTOS = () => {
        window.open(`${window.location.origin}/cgu_crealo.pdf`, "_blank");
        handleClose();
    };

    const buttonClassName = classNames({
        "navigation-link--collapsed": isCollapsed,
    });

    const content = (
        <>
            <div className="navigation-link__icon">
                <Help />
            </div>
            <span className="navigation-link__label">{t("NavigationBar.Help")}</span>
        </>
    );

    // For URSSAF, just open Crisp chat
    if (portalType === "urssaf") {
        return (
            <Tooltip title={isCollapsed ? t("NavigationBar.Help") : ""} placement="right" arrow>
                <span>
                    <NavigationButton
                        onClick={() => $crisp.push(["do", "chat:open"])}
                        variant={variant}
                        className={buttonClassName}
                    >
                        {content}
                    </NavigationButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <>
            <Tooltip title={isCollapsed ? t("NavigationBar.Help") : ""} placement="right" arrow>
                <span>
                    <NavigationButton onClick={handleClick} variant={variant} className={buttonClassName}>
                        {content}
                    </NavigationButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                elevation={0}
                className="mui-menu"
                sx={{ ".MuiList-root": { paddingBlock: 0 } }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: isCollapsed ? "right" : "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <MenuItem onClick={handleChangelog} style={menuItemStyle}>
                    {t("NavigationBar.Changelog")}
                </MenuItem>
                <MenuItem onClick={handleDocumentation} style={menuItemStyle}>
                    {t("NavigationBar.Documentation")}
                </MenuItem>
                <RenderIf show={portalType === "beneficiary"}>
                    <MenuItem onClick={handleTOS} style={menuItemStyle}>
                        {t("NavigationBar.TOS")}
                    </MenuItem>
                </RenderIf>
            </Menu>
        </>
    );
};

export default HelpMenu;
